import { APP_CONFIG } from '@/config/app.config';
import { cn } from '@/lib/utils';

/*
 * The Ghost Cable loading mark: a ghost that rises out of a pool of shadow and brightens until it glows,
 * then sinks back, on a loop. Replaces the spinning ring everywhere the app waits.
 *   size    — pixel width of the ghost (the full loader is a little taller for the shadow pool)
 *   compact — ghost only, no shadow pool or halo (for buttons and inline text); inherits currentColor
 * Motion lives in theme.css (.gc-ghost*). With reduced motion the ghost simply shows, fully lit.
 */
const BODY =
  'M12 28C12 15 21 6 32 6s20 9 20 22v26q-3.33-4-6.67 0t-6.67 0t-6.67 0t-6.67 0t-6.67 0t-6.67 0Z';
const EYES =
  'M21.8 26a3.2 4.2 0 1 0 6.4 0a3.2 4.2 0 1 0-6.4 0Z' +
  'M35.8 26a3.2 4.2 0 1 0 6.4 0a3.2 4.2 0 1 0-6.4 0Z' +
  'M29.6 37.5a2.4 3 0 1 0 4.8 0a2.4 3 0 1 0-4.8 0Z';

function GhostShape({ className }) {
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true" className={className}>
      <path d={BODY + EYES} fill="currentColor" fillRule="evenodd" />
    </svg>
  );
}

export default function GhostLoader({ size = 56, compact = false, className }) {
  if (compact) {
    return (
      <span
        aria-hidden="true"
        className={cn('inline-flex shrink-0 items-center justify-center', className)}
        style={{ width: size, height: size }}
      >
        <GhostShape className="gc-ghost gc-ghost-compact h-full w-full" />
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn('relative inline-flex shrink-0 items-end justify-center text-primary', className)}
      style={{ width: size * 1.6, height: size * 1.45 }}
    >
      <span className="gc-ghost-halo absolute left-1/2 top-[38%] rounded-full" style={{ width: size * 1.5, height: size * 1.5 }} />
      <span className="gc-ghost-pool absolute bottom-0 left-1/2 rounded-[50%]" style={{ width: size * 1.45, height: size * 0.34 }} />
      <span className="relative mb-[6%]" style={{ width: size, height: size }}>
        <GhostShape className="gc-ghost h-full w-full" />
      </span>
    </span>
  );
}

/** Full loading screen: the ghost with the app name under it. Same contract as AppLoader. */
export function GhostScreen({ fullScreen = false, label, className }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-col items-center justify-center gap-3 bg-background text-muted-foreground',
        fullScreen ? 'fixed inset-0' : 'min-h-[50vh] w-full',
        className,
      )}
    >
      <GhostLoader size={fullScreen ? 64 : 52} />
      <span className="gc-ghost-label text-sm tracking-wide">{label ?? APP_CONFIG.name}</span>
      <span className="sr-only">Loading</span>
    </div>
  );
}
